/**
 * Blockchain API Client
 * All endpoints for Hotel Admin Blockchain Dashboard
 */

import axios from 'axios';
import { BACKEND_URL, API } from '@/lib/backendUrl';

const API_BASE = BACKEND_URL || '';

// Create axios instance with default config
const blockchainApi = axios.create({
  baseURL: `${API_BASE}/api/blockchain`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// HEALTH & STATUS
// ============================================

export const getSystemHealth = async () => {
  const response = await blockchainApi.get('/health/status');
  return response.data;
};

// ============================================
// FEATURES MANAGEMENT
// ============================================

export const getHotelFeatures = async (hotelId) => {
  const response = await blockchainApi.get(`/features/hotel/${hotelId}`);
  return response.data;
};

export const toggleHotelFeature = async (hotelId, featureId) => {
  const response = await blockchainApi.post(`/features/hotel/${hotelId}/toggle`, {
    feature_id: featureId,
  });
  return response.data;
};

// ============================================
// SCORING & TIERS
// ============================================

export const calculateHotelScore = async (hotelId, pmsData = null) => {
  const response = await blockchainApi.post(`/scoring/calculate/${hotelId}`, pmsData || {});
  return response.data;
};

export const getHotelScore = async (hotelId) => {
  const response = await blockchainApi.get(`/scoring/${hotelId}`);
  return response.data;
};

export const getScoreHistory = async (hotelId, limit = 30) => {
  const response = await blockchainApi.get(`/scoring/history/${hotelId}`, {
    params: { limit },
  });
  return response.data;
};

export const runMonthlyScoring = async () => {
  const response = await blockchainApi.post('/scoring/run-monthly');
  return response.data;
};

// ============================================
// STAKING & REWARDS
// ============================================

export const getStakingInfo = async (hotelId) => {
  // Note: This endpoint needs to be implemented in backend
  // Placeholder for now
  const response = await blockchainApi.get(`/staking/${hotelId}`);
  return response.data;
};

export const getRewardsHistory = async (hotelId, limit = 50) => {
  const response = await blockchainApi.get(`/staking/rewards/${hotelId}`, {
    params: { limit },
  });
  return response.data;
};

// ============================================
// WALLETS
// ============================================

export const getHotelWallets = async (hotelId) => {
  // This will call the DB to get wallet addresses
  // Format: { treasury: '0x...', bitsy: '0x...', marina: '0x...' }
  const response = await blockchainApi.get(`/wallets/${hotelId}`);
  return response.data;
};

export const getWalletBalance = async (address) => {
  const response = await blockchainApi.get(`/wallets/balance/${address}`);
  return response.data;
};

// ============================================
// AI AGENT TELEMETRY
// ============================================

export const getAgentTelemetry = async (hotelId, agentName = null) => {
  const params = agentName ? { agent: agentName } : {};
  const response = await blockchainApi.get(`/agents/telemetry/${hotelId}`, { params });
  return response.data;
};

export const getAgentRecentActions = async (hotelId, agentName, limit = 20) => {
  const response = await blockchainApi.get(`/agents/actions/${hotelId}`, {
    params: { agent: agentName, limit },
  });
  return response.data;
};

// ============================================
// EVENTS & INDEXING
// ============================================

export const getRecentEvents = async (hotelId = null, limit = 50) => {
  const params = { limit };
  if (hotelId) params.hotel_id = hotelId;
  
  const response = await blockchainApi.get('/events/recent', { params });
  return response.data;
};

export const getEventsByHotel = async (hotelId, eventType = null, limit = 100) => {
  const params = { limit };
  if (eventType) params.event_type = eventType;
  
  const response = await blockchainApi.get(`/events/hotel/${hotelId}`, { params });
  return response.data;
};

// ============================================
// AUTOMATION STATUS
// ============================================

export const getAutomationStatus = async () => {
  const response = await blockchainApi.get('/automation/status');
  return response.data;
};

export const triggerManualScoring = async () => {
  const response = await blockchainApi.post('/automation/trigger/scoring');
  return response.data;
};

// ============================================
// HOTEL REGISTRATION (Admin)
// ============================================

export const registerHotel = async (hotelId, treasuryAddress) => {
  const response = await blockchainApi.post('/hotels/register', {
    hotel_id: hotelId,
    treasury_address: treasuryAddress,
  });
  return response.data;
};

export const getRegisteredHotels = async () => {
  const response = await blockchainApi.get('/hotels/list');
  return response.data;
};

// ============================================
// ERROR HANDLER
// ============================================

blockchainApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const customError = {
      message: error.response?.data?.detail || error.message || 'Unknown error',
      status: error.response?.status,
      data: error.response?.data,
    };
    
    // Log for debugging
    console.error('[Blockchain API Error]', customError);
    
    return Promise.reject(customError);
  }
);

export default blockchainApi;
